import { restoreErrorMessage } from "@/api/archived";
import { useFlowSessionById, useUpdateFlowSession } from "@/api/flow_sessions";
import { useHarnessCapabilities } from "@/api/harnessCapabilities";
import { isApiError } from "@/api/utils";
import {
  defaultModelFor,
  harnessSeatVisible,
  modelSwitchAllowed,
} from "@/constants/harnesses";
import { HarnessDraft } from "@/hooks/useHarnessDraft";
import { Flags } from "@/providers/LaunchDarklyProvider";
import {
  FlowSession,
  HARNESS_IDS,
  HarnessCapability,
  HarnessCapabilityModel,
  HarnessId,
} from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import toast from "react-hot-toast";

export interface UseHarnessControlParams {
  /**
   * The stamped session this composer sends into, if any. Until its first message the stamp is
   * unspent and a pick writes through to the row; after that the harness is locked — every
   * later turn runs on it.
   */
  flowSessionId?: string;
  /** Draft choice, used only while there is no session. */
  draft?: HarnessDraft;
  /** Set the draft. Its presence is what makes a session-less control interactive. */
  onDraftChange?: (draft: HarnessDraft) => void;
}

/** One row of the model menu: the harness's own model, plus whether this chat may move to it. */
export interface HarnessModelOption extends HarnessCapabilityModel {
  disabled: boolean;
}

export interface HarnessControl {
  /** Whether the seat renders at all. */
  visible: boolean;
  /**
   * True once the session's first message has run: the harness is read-only, the model still
   * switchable. A session that exists but has no messages yet is not locked — the picker stays
   * live and writes through — unless the harness-runtime flag or the catalog is unavailable,
   * where a pick has nowhere to land.
   */
  locked: boolean;
  harness: HarnessId;
  capabilities: HarnessCapability[];
  capability: HarnessCapability | undefined;
  /** The selected model, or undefined on a harness that takes none (cohesive). */
  model: string | undefined;
  /** The model menu's rows, in catalog order, with the ones this chat can't move to disabled. */
  models: HarnessModelOption[];
  /** Whether any row is disabled, so the menu can say once why. */
  modelSwitchLocked: boolean;
  setHarness: (harness: HarnessId) => void;
  setModel: (model: string) => void;
  /**
   * The model to stamp on the next message, or undefined to leave the session's alone. Set only
   * where the user actually switched: sending the resolved default would pin a session that was
   * deliberately left to resolve the harness default per turn.
   *
   * A draft never sends one — its choice rides the session create instead, so a composer that
   * minted its session early can't send a model minted for a harness it didn't get. A zero-turn
   * session's pick writes through to the row directly, so it too rides no message.
   */
  sendModel: string | undefined;
}

/**
 * Resolves the composer's harness + model from the two places they can live: the flow session
 * once one exists, and the caller's draft until then.
 *
 * The stamp is spent by the first turn, not by the session's existence: a chat with no messages
 * has resolved no provider credentials, so on a zero-turn session the picker stays live and a
 * pick writes through to the row via the update endpoint. Mid-chat model switches are instead
 * held here and carried by the next message.
 */
export function useHarnessControl({
  flowSessionId,
  draft,
  onDraftChange,
}: UseHarnessControlParams): HarnessControl {
  const flags = useFlags<Flags>();
  // Shares the query key used elsewhere, so React Query dedupes — no extra fetch.
  const { data: flowSession } = useFlowSessionById(flowSessionId || "");
  const { data: capabilities } = useHarnessCapabilities();
  const queryClient = useQueryClient();
  const updateFlowSession = useUpdateFlowSession({
    onSuccess: (data) => {
      // Land the stamp in the cache immediately; the shared invalidation resyncs the rest.
      // Merged from the server's row, not the request: the response is what actually landed,
      // and the explicit keys carry an omitempty-cleared model over the cached one.
      queryClient.setQueryData<FlowSession>(["flowSession", data.id], (prev) =>
        prev ? { ...prev, harness: data.harness, model: data.model } : prev,
      );
      // The persisted draft, where one backs this composer, follows a pick only once it lands
      // — mirrored so a later session-less mount does not resurrect the old choice, and only
      // here so a refused write cannot seed it with a choice the session never took.
      onDraftChange?.({
        harness: data.harness ?? HARNESS_IDS.COHESIVE,
        model: data.model,
      });
    },
    onError: (err, variables) => {
      toast.error(restoreErrorMessage(err, "Failed to update chat"));
      // A 422 means the stamp was spent under us (a first message landed): resync the lock.
      // The interceptor rethrows failures as ApiError, which carries the code as .status; a
      // raw AxiosError (a request that never reached it) keeps .response.
      const status = isApiError(err) ? err.status : err.response?.status;
      if (status === 422) {
        queryClient.invalidateQueries({
          queryKey: ["flowSession", variables.flowSessionId],
        });
      }
    },
  });
  // Tagged with the session it was picked for: this composer stays mounted across chats
  // (floating chat), and an untagged switch would follow the user into the next one.
  const [pending, setPending] = useState<{
    flowSessionId?: string;
    model: string;
  }>();

  // Status is the zero-turn proxy — a session leaves `initialized` when its first message
  // lands — and the server enforces the real invariant (422) if this view is stale. A session
  // not loaded (or errored) reads as locked, so no picker is offered on a stamp we cannot see;
  // so does one whose flag or catalog is gone, where a write-through would only be refused.
  const restampable =
    !!flowSession &&
    flowSession.status === "initialized" &&
    !!flags.harnessRuntime &&
    !!capabilities;
  const locked = !!flowSessionId && !restampable;
  // Queued switches ride the next message only once the stamp is spent; before that a pick
  // writes through, so a queued value has nothing to say.
  const pendingModel =
    locked && pending && pending.flowSessionId === flowSessionId
      ? pending.model
      : undefined;
  const harness =
    (flowSessionId ? flowSession?.harness : draft?.harness) ??
    HARNESS_IDS.COHESIVE;
  const capability = capabilities?.find((c) => c.id === harness);

  // What the session's next turn would run: its pinned model, else the harness default. The
  // server measures a switch against this, not against one the user has queued but not sent.
  const turnModel = flowSession?.model ?? defaultModelFor(capability);
  const model = flowSessionId ? (pendingModel ?? turnModel) : draft?.model;

  // A pick is bound by nothing but the catalog until the first turn — the lock belongs to a
  // sandbox that resolved its provider credentials once, and a chat with no messages has none.
  const models: HarnessModelOption[] = (capability?.models ?? []).map(
    (option) => ({
      ...option,
      disabled: locked && !modelSwitchAllowed(capability, turnModel, option.id),
    }),
  );

  // A session-less composer whose owner hands down `onDraftChange` holds the choice until the
  // session is minted. Without that handler there is nothing to drive and nothing to state, so
  // the seat stays empty rather than rendering a picker that does nothing. With a session the
  // row itself holds the choice, so no handler is needed — but a session that has not loaded,
  // or that errored, has nothing to state and must not fall back to the draft: the stamp may
  // already be spent, and a picker there would offer a choice with no effect.
  const hasSomethingToSay = flowSessionId ? !!flowSession : !!onDraftChange;

  return {
    visible:
      hasSomethingToSay &&
      harnessSeatVisible(
        flowSession?.harness,
        !!flags.harnessRuntime,
        capabilities,
      ),
    locked,
    harness,
    capabilities: capabilities ?? [],
    capability,
    model,
    models,
    modelSwitchLocked: models.some((option) => option.disabled),
    // Picking the row that is already checked is how the menu is dismissed without changing
    // anything, so it must not reset the model the user chose under that harness.
    setHarness: (next) => {
      if (next === harness) return;
      const nextModel = defaultModelFor(
        capabilities?.find((c) => c.id === next),
      );
      if (!locked && flowSessionId) {
        updateFlowSession.mutate({
          flowSessionId,
          data: { harness: next, model: nextModel },
        });
        return;
      }
      onDraftChange?.({ harness: next, model: nextModel });
    },
    setModel: (next) => {
      // Landing on what the pill already shows pins nothing: on a chat that pinned no model
      // that is the resolved default, deliberately left to resolve per turn.
      if (next === model) return;
      if (locked) {
        setPending({ flowSessionId, model: next });
        return;
      }
      if (flowSessionId) {
        updateFlowSession.mutate({
          flowSessionId,
          data: { harness, model: next },
        });
        return;
      }
      onDraftChange?.({ harness, model: next });
    },
    // Measured against the turn, not against the pin: a pick that lands back on what the
    // session already runs is not a switch, however it got there. On a chat that pinned
    // nothing that is the resolved default, and sending it would fix a chat deliberately
    // left to resolve that default every turn.
    sendModel:
      pendingModel && pendingModel !== turnModel ? pendingModel : undefined,
  };
}
